import { CSSProperties, useEffect, useMemo, useState } from "react";
import {
  LocalizableString,
  Question,
  SurveyModel,
  ValueChangedEvent,
} from "survey-core";
import { SurveyElementBase } from "survey-react-ui";
import { LicensePlate } from "../../../ui/src";
import { useQuery } from "@tanstack/react-query";
import { Alert, Typography } from "antd";
import { isAxiosError } from "axios";

export class LicensePlateQuestionModel extends Question {
  getType() {
    return "licensePlate";
  }

  get vehicle(): any {
    return this.getPropertyValue("vehicle");
  }
  set vehicle(val: any) {
    this.setPropertyValue("vehicle", val);
  }
}

interface LicensePlateQuestionProps {
  question: LicensePlateQuestionModel;
}

type apiResponse = {
  vehicle: {
    licensePlate: {
      country: string;
      identifier: string;
      identifierFormatted: string;
    };
    brand: string;
    model: string;
    buildYear: number;
    listPrice: number;
    firstRegistrationAt: string | null;
    lastRegistrationAt: string | null;
  };
  fleetLeaseLabel?: {
    displayName: string;
    displayLogoUrl: string;
    serviced: boolean;
    unservicedMessage: null | string;
  } | null;
  fleetRecordId?: string | null;
  photoCaptureAllowed?: boolean | null;
};

export const LicensePlateQuestion = (props: LicensePlateQuestionProps) => {
  const [licensePlateInput, setLicensePlateInput] = useState<string>(
    props.question.value?.identifier ?? "",
  );
  const [fleetMessage, setFleetMessage] = useState<string | null>(null);
  const survey = props.question.survey as SurveyModel;
  const vehicleValueKey = `${props.question.name}__vehicle`;

  const canQuery = useMemo(
    () =>
      !!licensePlateInput &&
      licensePlateInput.replace(/-/g, "").length >= 6 &&
      licensePlateInput !== props.question.value?.identifier,
    [licensePlateInput, props.question.value],
  );

  const evaluationGroupQuery = useQuery({
    retry: false,
    enabled: canQuery,
    queryKey: ["fleet", "NL", licensePlateInput.replace(/-/g, "")],
    queryFn: async (): Promise<apiResponse> => {
      if (props.question.evaluationGroupId === undefined) {
        return survey!
          .api!.get(`/license-plate/NL/${licensePlateInput}`)
          .then(({ data }) => ({
            vehicle: data,
          }));
      }
      return survey!
        .api!.post(
          `/driver/evaluation-engine/${props.question.evaluationGroupId}`,
          {
            identifier: licensePlateInput,
            country: "NL",
          },
        )
        .then(({ data: { outputs } }) => ({
          fleetLeaseLabel: outputs.fleet_record?.fleetLeaseLabel,
          fleetRecordId: outputs.fleet_record?.id,
          photoCaptureAllowed: outputs.photoCaptureAllowed,
          vehicle: outputs.vehicle_registration,
        }));
    },
  });

  useEffect(() => {
    if (props.question.value) {
      setLicensePlateInput(props.question.value.identifier || "");
    }
  }, [props.question.value]);

  useEffect(() => {
    if (!survey) return;

    const vehicle = survey.getValue(vehicleValueKey);
    if (vehicle) {
      props.question.vehicle = vehicle;
    }

    const handleValueChange = (_: SurveyModel, options: ValueChangedEvent) => {
      if (options.name === vehicleValueKey) {
        props.question.vehicle = options.value ?? null;
      }
    };

    survey.onValueChanged.add(handleValueChange);
    return () => {
      survey.onValueChanged.remove(handleValueChange);
    };
  }, [survey, vehicleValueKey, props.question]);

  useEffect(() => {
    if (!evaluationGroupQuery.data) {
      props.question.vehicle = null;
      props.question.value = null;
      return;
    }

    const vehicleInfo = {
      brand: evaluationGroupQuery.data.vehicle.brand ?? "",
      model: evaluationGroupQuery.data.vehicle.model ?? "",
      buildYear: evaluationGroupQuery.data.vehicle.buildYear ?? "",
    };
    props.question.vehicle = vehicleInfo;
    survey?.setValue(vehicleValueKey, vehicleInfo);

    props.question.value = {
      country: evaluationGroupQuery.data.vehicle?.licensePlate?.country,
      identifier:
        evaluationGroupQuery.data.vehicle?.licensePlate?.identifierFormatted,
    };

    const identifierFormatted =
      evaluationGroupQuery.data.vehicle?.licensePlate.identifierFormatted || "";
    if (identifierFormatted && identifierFormatted !== licensePlateInput) {
      setLicensePlateInput(identifierFormatted);
    }
  }, [evaluationGroupQuery.data, props.question]);

  //TODO: Move this logic outside of license plate question
  const handleFleetCheckState = ({
    success,
    message,
    fleetRecordId,
    photoCaptureAllowed,
  }: {
    success: boolean;
    message?: string | null;
    fleetRecordId?: string | null;
    photoCaptureAllowed?: boolean | null;
  }) => {
    if (success) {
      survey.setValue("unservicedVehicle", false);
      setFleetMessage(null);
    } else {
      survey.setValue("unservicedVehicle", true);
      setFleetMessage(message ?? "Dit voertuig is niet bij ons bekend.");
    }
    survey.setValue("fleetRecord", fleetRecordId ?? null);
    survey.setValue("photoCaptureAllowed", photoCaptureAllowed ?? true);
  };

  useEffect(() => {
    if (
      !props.question.fleetCheck ||
      evaluationGroupQuery.status === "pending"
    ) {
      return;
    }

    if (evaluationGroupQuery.isError) {
      const error = evaluationGroupQuery.error;
      if (isAxiosError(error) && error.status === 404) {
        handleFleetCheckState({
          success: false,
          message: error?.response?.data?.detail,
        });
      }
    } else if (
      evaluationGroupQuery.data &&
      evaluationGroupQuery.data.fleetRecordId !== undefined &&
      !evaluationGroupQuery.data.fleetLeaseLabel?.serviced
    ) {
      handleFleetCheckState({
        success: false,
        message: evaluationGroupQuery.data.fleetLeaseLabel?.unservicedMessage,
        fleetRecordId: evaluationGroupQuery.data.fleetRecordId,
        photoCaptureAllowed: evaluationGroupQuery.data.photoCaptureAllowed,
      });
    } else if (evaluationGroupQuery.data) {
      handleFleetCheckState({
        success: true,
        fleetRecordId: evaluationGroupQuery.data!.fleetRecordId,
        photoCaptureAllowed: evaluationGroupQuery.data.photoCaptureAllowed,
      });
    }
  }, [evaluationGroupQuery.status]);

  const handleChange = (value: string) => {
    const normalised = value.trim().toUpperCase();
    setLicensePlateInput(normalised);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          width: "100%",
          gap: "16px",
        }}
      >
        <div style={{ flex: "1 1 320px", paddingBottom: "34px" }}>
          <div style={{ maxWidth: "260px" }}>
            <LicensePlate
              country="NL"
              size="large"
              isLoading={evaluationGroupQuery.isFetching}
            >
              <input
                id={props.question.inputId}
                disabled={props.question.isDisabledAttr}
                readOnly={props.question.isReadOnlyAttr}
                type={props.question.inputType}
                style={
                  {
                    ...props.question.inputStyle,
                    width: "100%",
                    border: "none",
                    background: "transparent",
                    outline: "none",
                    fontSize: "26px",
                    fontWeight: "900",
                  } as CSSProperties
                }
                value={licensePlateInput || ""}
                step={props.question.renderedStep}
                list={props.question.dataListId}
                onBlur={(e) => props.question.onBlur(e)}
                onFocus={(e) => props.question.onFocus(e)}
                onChange={(e) => handleChange(e.target.value)}
                onClick={props.question.readOnlyBlocker}
                onPointerDown={props.question.readOnlyBlocker}
                onKeyUp={props.question.onKeyUp}
                onKeyDown={props.question.onKeyDown}
                onCompositionUpdate={(event) =>
                  props.question.onCompositionUpdate(event.nativeEvent)
                }
                aria-required={props.question.a11y_input_ariaRequired}
                aria-label={props.question.a11y_input_ariaLabel}
                aria-labelledby={props.question.a11y_input_ariaLabelledBy}
                aria-describedby={props.question.a11y_input_ariaDescribedBy}
                aria-invalid={props.question.a11y_input_ariaInvalid}
                aria-errormessage={props.question.a11y_input_ariaErrormessage}
                autoCapitalize="characters"
              />
            </LicensePlate>
          </div>
        </div>
        {props.question.vehicle?.brand || props.question.vehicle?.model ? (
          <div
            style={{
              flex: "1 1 320px",
              position: "relative",
            }}
          >
            <span style={{ position: "absolute", top: "-30px" }}>
              <Typography.Text style={{ color: "#999" }}>
                Voertuig merk / model
              </Typography.Text>
            </span>
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: "8px",
                padding: "12px 18px",
                margin: 0,
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                columnGap: "40px",
              }}
            >
              <Typography.Text style={{ fontWeight: 600 }}>
                Merk:
              </Typography.Text>
              <Typography.Text>
                {props.question.vehicle?.brand || "-"}
              </Typography.Text>
              <Typography.Text style={{ fontWeight: 600 }}>
                Model:
              </Typography.Text>
              <Typography.Text>
                {props.question.vehicle?.model || "-"} (
                {props.question.vehicle?.buildYear || "-"})
              </Typography.Text>
            </div>
          </div>
        ) : null}
      </div>
      {fleetMessage
        ? (() => {
            const locString = new LocalizableString(survey, true);
            locString.text = fleetMessage;
            return (
              <Alert
                description={SurveyElementBase.renderLocString(locString)}
                type="error"
                style={{ marginBottom: 20, marginTop: 20 }}
              />
            );
          })()
        : null}
    </div>
  );
};
